import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import useLocalStorage from "use-local-storage";

import { EncodedPolyLine, Map, MarkerProps, PolyLine } from "@cofano/cck";

import { Box, Tab, Tabs, useTheme } from "@mui/material";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import RoomIcon from "@mui/icons-material/Room";
import RouteIcon from "@mui/icons-material/Route";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PushPinIcon from "@mui/icons-material/PushPin";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";

import useTabs from "components/hooks/useTabs";
import Helmet from "components/helmet/Helmet";

import { Position } from "__generated__/graphql";

import MapRouteTap from "./components/tabs/MapRouteTab";
import MapLocationsTab from "./components/tabs/MapLocationsTab";
import MapWaterLevelTab from "./components/tabs/MapWaterLevelsTab";
import MapSimulatorTab from "./components/tabs/MapSimulatorTab";
import MapShipsTab from "./components/tabs/MapShipsTab";
import MapLegend from "./components/legend/MapLegend";
import MapSettings from "./components/settings/MapSettings";

import { CallTimeSource, MapTabs, getMapCenterPosition } from "./utils/MapUtils";
import styles from "./utils/Styles.json";
import stylesDark from "./utils/StylesDark.json";

type Settings = {
	bounds: boolean;
	clusterRadius?: number;
	markers: MarkerProps[];
	polylines: PolyLine[];
	encodedPolylines: EncodedPolyLine[];
}

const Maps = () => {
	const [ _, setSearchParams ] = useSearchParams();

	const theme = useTheme();

	const session = useStoreState(store => store.session);
	const mapCenter = getMapCenterPosition(session);

	const [ callTimeSource, saveCallTimeSource ] = useLocalStorage<CallTimeSource>("mapsCallTimeSource", CallTimeSource.Calculated);

	const [ collapsed, setCollapsed ] = useState(false);
	const [ position, setPosition ] = useState<Position | undefined>(undefined);
	const [ settings, setSettings ] = useState<Settings>({
		bounds: true,
		clusterRadius: undefined,
		markers: [],
		polylines: [],
		encodedPolylines: [],
	});

	const { selectedTab, selectTab } = useTabs<MapTabs>({
		defaultSelected: MapTabs.Ships,
	});

	return (
		<Box
			sx={{
				bottom: 0,
				left: 250,
				overflow: "hidden",
				position: "absolute",
				right: 0,
				top: 50,
				transition: "all 0.2s ease",

				"& .map": {
					height: "100%",
					width: "100%",
				},

				"body.sidebar-collapsed &": {
					left: 50,
				},
			}}>
			<Map
				center={mapCenter}
				disableBounds={!settings.bounds}
				clusterRadius={settings.clusterRadius}
				styles={theme.palette.mode === "dark" ? stylesDark : styles}
				zoom={13}
				mapTypeControl={true}
				markers={settings.markers}
				polyLines={settings.polylines}
				encodedPolyLines={settings.encodedPolylines}
				apiKey="AIzaSyAFEhlhSJ7c85rDGz1JX99veD0sshXx_xs"
				onClick={(latitude: number, longitude: number) => {

					if (selectedTab === MapTabs.Simulator) {
						setPosition({ latitude, longitude });
					}
				}} />

			<Helmet title={intl.formatMessage({ id: "SHIPS_MAPS" })} />

			{selectedTab === MapTabs.Ships && (
				<MapLegend
					type={MapTabs.Ships}
					callTimeSource={callTimeSource} />
			)}

			{selectedTab === MapTabs.Locations && (
				<MapLegend
					type={MapTabs.Locations}
					callTimeSource={callTimeSource} />
			)}

			<Box
				className={`sidebar ${collapsed ? "collapsed" : ""}`}
				sx={{
					maxHeight: "calc(100vh - 80px)",
					position: "absolute",
					right: collapsed ? -450 : 10,
					top: 10,
					transition: "all 0.3s ease;",
					width: 450,
					zIndex: 5,
				}}>
				<Box
					onClick={() => setCollapsed(!collapsed)}
					sx={{
						alignItems: "center",
						background: theme.palette.background.paper,
						boxShadow: 1,
						cursor: "pointer",
						display: "flex",
						height: 48,
						justifyContent: "center",
						left: -22,
						position: "absolute",
						top: 60,
						transition: "all 0.3s ease",
						width: 22,
						zIndex: 1,
					}}>
					{collapsed ? <KeyboardArrowLeftIcon color="action" /> : <KeyboardArrowRightIcon color="action" />}
				</Box>

				<Box
					sx={{
						background: theme.palette.background.paper,
						boxShadow: 1,
						position: "relative",
						zIndex: 3,
					}}>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							value={selectedTab}
							onChange={(event, newValue) => {
								setSettings({
									bounds: true,
									clusterRadius: undefined,
									markers: [],
									polylines: [],
									encodedPolylines: [],
								});

								// Clear all query parameters from the current tab
								setSearchParams((prev) => {
									const latestParams = new URLSearchParams(prev);
									latestParams.delete("ship");
									latestParams.delete("fleets");
									latestParams.delete("types");
									latestParams.delete("location");
									latestParams.delete("showGeofences");
									return latestParams;
								}, {
									replace: true,
								});

								// Prevents the newly mounted tab component from reading outdated searchParams.
								setTimeout(() => {
									selectTab(newValue);
								}, 0);
							}}>
							<Tab icon={<DirectionsBoatIcon />} value={MapTabs.Ships} />
							<Tab icon={<RoomIcon />} value={MapTabs.Locations} />
							<Tab icon={<RouteIcon />} value={MapTabs.Route} />
							<Tab icon={<ShowChartIcon />} value={MapTabs.Waterlevels} />
							<Tab icon={<PushPinIcon />} value={MapTabs.Simulator} />
							<Tab icon={<SettingsIcon />} value={MapTabs.Settings} />
						</Tabs>
					</Box>

					<Box sx={{ maxHeight: "calc(100vh - 128px)", overflowY: "auto" }}>
						{selectedTab === MapTabs.Ships && (
							<MapShipsTab
								key={selectedTab}
								callTimeSource={callTimeSource}
								onChange={(markers, encodedPolylines, fitBounds = true) => {
									setSettings({
										bounds: fitBounds,
										clusterRadius: 50,
										markers: markers,
										polylines: [],
										encodedPolylines: encodedPolylines,
									});
								}} />
						)}

						{selectedTab === MapTabs.Locations && (
							<MapLocationsTab
								key={selectedTab}
								onChange={(markers, encodedPolylines) => {
									setSettings({
										bounds: true,
										clusterRadius: 50,
										markers: markers,
										polylines: [],
										encodedPolylines: encodedPolylines,
									});
								}} />
						)}

						{selectedTab === MapTabs.Route && (
							<MapRouteTap
								onChange={(markers, encodedPolylines) => {
									setSettings({
										bounds: true,
										clusterRadius: 50,
										markers: markers,
										polylines: [],
										encodedPolylines: encodedPolylines,
									});
								}} />
						)}

						{selectedTab === MapTabs.Waterlevels && (
							<MapWaterLevelTab
								onChange={(markers) => {
									setSettings({
										bounds: true,
										clusterRadius: 50,
										markers: markers,
										polylines: [],
										encodedPolylines: [],
									});
								}} />
						)}

						{selectedTab === MapTabs.Simulator && (
							<MapSimulatorTab
								onChange={(markers) => {
									setSettings({
										bounds: false,
										clusterRadius: 50,
										markers: markers,
										polylines: [],
										encodedPolylines: [],
									});
								}}
								position={position}
							/>
						)}

						{selectedTab === MapTabs.Settings && (
							<MapSettings
								callTimeSource={callTimeSource}
								saveCallTimeSource={saveCallTimeSource} />
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default Maps;
